import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  DxPopupModule,
  DxSelectBoxModule,
  DxTextBoxModule,
  DxTreeListModule,
} from 'devextreme-angular';
import { AppGlobals } from 'src/app/app.globals';
import { FeatureFlags } from 'src/app/interfaces/enums/feature-flag';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { GenericPopupComponent, PopupButton } from 'src/app/components/generic-popup/generic-popup.component';
import { ValidationToastComponent } from 'src/app/components/validation-toast/validation-toast.component';
import { SandboxService } from 'src/app/services/sandbox.service';
import { TemplatesService } from 'src/app/services/templates.service';

import { SearchableDropdownDirective } from '../../../../directives/searchable-dropdown.directive';
export type TreeType = 'site' | 'project' | 'sandbox-site' | 'sandbox-project';

@Component({
    selector: 'app-folder-tree-config',
    imports: [
        CommonModule,
        FormsModule,
        DxTreeListModule,
        DxPopupModule,
        DxTextBoxModule,
        DxSelectBoxModule,
        GpdButtonComponent,
        SvgIconComponent,
        GenericPopupComponent,
        ValidationToastComponent,
        SearchableDropdownDirective,
    ],
    templateUrl: './folder-tree-config.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./folder-tree-config.component.css']
})
export class FolderTreeConfigComponent implements OnInit, OnChanges {
  @Input() treeTypeInput?: TreeType;
  @Input() showHeader: boolean = true;

  treeType: TreeType = 'site';

  // tree data: single virtual root with the real hierarchy below it
  treeData: any[] = [];
  expandedKeys: number[] = [];
  flatFolders: any[] = []; // flat list incl. computed paths, for the picker

  // add/rename popup
  editPanelVisible = false;
  selectedFolder: any = {};
  currentPanelTitle = '';
  addFolderText = $localize`:@@addNewFolder:Create folder`;
  addSubFolderText = $localize`:@@addNewSubFolder:Add New Sub-Folder`;
  editFolderText = $localize`:@@editFolder:Rename folder`;
  saveFolderText = $localize`:@@saveFolder:Save`;

  // delete flow
  deleteMode: 'none' | 'simple' | 'typename' | 'blocked' = 'none';
  deleteInfo: any = { documentCount: 0, dependentTasks: [] };
  typedFolderName = '';
  readonly deleteConfirmPrefix = $localize`:@@deleteFolderSimpleConfirmPrefix:Are you sure that you want to remove the folder`;
  readonly deleteConfirmSuffix = $localize`:@@deleteFolderSimpleConfirmSuffix:?`;
  readonly deleteTypeNameConfirmPrefix = $localize`:@@deleteFolderTypeNameConfirmPrefix:Do you really want to delete the folder`;
  readonly deleteTypeNameConfirmSuffix = $localize`:@@deleteFolderTypeNameConfirmSuffix:?`;
  readonly blockedInstruction = `<p>${$localize`:@@deleteFolderBlockedInstructionLine1:Please first change the destination folder of the affected tasks.`}</p><p>${$localize`:@@deleteFolderBlockedInstructionLine2:You can do this under`} <strong>${$localize`:@@deleteFolderBlockedInstructionLine2Path:Project Configuration → Tasks`}</strong>.</p><p>${$localize`:@@deleteFolderBlockedInstructionLine3:Micro tasks can be adjusted in the respective project.`}</p>`;
  readonly deleteHint = $localize`:@@delete:Delete`;
  readonly hintTitle = $localize`:@@folderStructureHintTitle:Note on the folder structure`;
  readonly hintMessage = $localize`:@@folderStructureHintText:This folder structure is created automatically for each new project. You can create, rename or delete folders. Subfolders can be created by clicking the plus symbol next to each folder.`;

  // special-folder picker
  settings: any = { propertyFolderId: null, imagesFolderId: null, commentsFolderId: null, mastrFolderId: null };
  pickerValue: number | null = null;
  commentsPickerValue: number | null = null;
  mastrPickerValue: number | null = null;
  pickerTooltipSite = $localize`:@@propertyFolderTooltip:Please specify where the property documents should be filed in the site folder.`;
  pickerTooltipProject = $localize`:@@imagesFolderTooltip:Please specify where the images should be placed in the project folder.`;
  pickerTooltipComments = $localize`:@@commentsFolderTooltip:Files attached to task notes are automatically saved to this folder.`;
  pickerTooltipMastr = $localize`:@@mastrFolderTooltip:The conformity confirmations of the MaStR entries are stored here. Without a selection: project folder.`;

  private saving = false;

  readonly virtualRootId = -1;

  get mastrEnabled(): boolean { return AppGlobals.UserCan(FeatureFlags.MaStR); }

  get isSaving(): boolean { return this.saving; }

  get isSandbox(): boolean {
    return this.treeType === 'sandbox-site' || this.treeType === 'sandbox-project';
  }

  get isSandboxSite(): boolean {
    return this.treeType === 'sandbox-site';
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly service: TemplatesService,
    private readonly sandboxService: SandboxService,
  ) {}

  ngOnInit(): void {
    if (this.treeTypeInput != null) {
      this.treeType = this.treeTypeInput;
      this.reload();
      return;
    }
    this.route.data.subscribe(data => {
      this.treeType = data['treeType'] ?? 'site';
      this.reload();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['treeTypeInput'] && !changes['treeTypeInput'].firstChange) {
      this.treeType = this.treeTypeInput!;
      this.reload();
    }
  }

  get isSite(): boolean {
    return this.treeType === 'site';
  }

  get folderStructureDescription(): string {
    if (this.treeType === 'site') {
      return $localize`:@@folderStructureDescriptionSite:Add folder names to organize and manage the document structure for site folders.`;
    }
    return $localize`:@@folderStructureDescriptionProject:Add folder names to organize and manage the document structure for project folders.`;
  }

  get deleteModeIsTypeName(): boolean {
    return this.deleteMode === 'typename';
  }
  set deleteModeIsTypeName(value: boolean) {
    if (!value) this.deleteMode = 'none';
  }

  get deleteModeIsBlocked(): boolean {
    return this.deleteMode === 'blocked';
  }
  set deleteModeIsBlocked(value: boolean) {
    if (!value) this.deleteMode = 'none';
  }

  async reload(): Promise<void> {
    this.editPanelVisible = false;
    this.deleteMode = 'none';
    this.typedFolderName = '';

    try {
      let folderResult: any;
      if (this.isSandbox) {
        folderResult = this.isSandboxSite
          ? await this.sandboxService.getSandboxSiteFolders({})
          : await this.sandboxService.getSandboxProjectFolders({});
      } else {
        folderResult = await this.service.getDocumentFolders({
          IncludeGridConnectionFolders: this.isSite,
          IncludeProjectFolders: !this.isSite,
        });
      }
      if (folderResult.hasError === true) {
        AppGlobals.ShowErrorMessage(folderResult.feedback);
        return;
      }

      const rootName = this.isSandboxSite
        ? $localize`:@@folderTreeRootSandboxSite:Sandbox site`
        : this.isSandbox
          ? $localize`:@@folderTreeRootSandboxProject:Sandbox project`
          : this.isSite
            ? $localize`:@@folderTreeRootSite:Site`
            : $localize`:@@folderTreeRootProject:Project`;

      this.flatFolders = folderResult.folderList ?? [];
      const hierarchy = folderResult.folderHierarchy ?? [];
      this.treeData = [
        {
          id: this.virtualRootId,
          name: rootName,
          isVirtualRoot: true,
          subFolders: hierarchy,
        },
      ];
      this.expandedKeys = this.collectIds(this.treeData);

      if (!this.isSandbox) {
        const settingsResult = await this.service.getFolderSettings({});
        if (settingsResult.hasError === true) {
          AppGlobals.ShowErrorMessage(settingsResult.feedback);
          return;
        }
        this.settings = {
          propertyFolderId: settingsResult.propertyFolderId ?? null,
          imagesFolderId: settingsResult.imagesFolderId ?? null,
          commentsFolderId: settingsResult.commentsFolderId ?? null,
          mastrFolderId: settingsResult.mastrFolderId ?? null,
        };
        this.pickerValue = this.isSite ? this.settings.propertyFolderId : this.settings.imagesFolderId;
        this.commentsPickerValue = this.isSite ? null : this.settings.commentsFolderId;
        this.mastrPickerValue = this.isSite ? null : this.settings.mastrFolderId;
      }
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  addTopLevelFolder(): void {
    this.selectedFolder = { id: null, name: '', parentId: null };
    this.currentPanelTitle = this.addFolderText;
    this.editPanelVisible = true;
  }

  addSubFolder(folder: any): void {
    this.selectedFolder = { id: null, name: '', parentId: folder.id };
    this.currentPanelTitle = this.addSubFolderText;
    this.editPanelVisible = true;
  }

  renameFolder(folder: any): void {
    this.selectedFolder = { id: folder.id, name: folder.name, parentId: folder.parentId ?? null };
    this.currentPanelTitle = this.editFolderText;
    this.editPanelVisible = true;
  }

  get deletePopupButtons(): PopupButton[] {
    return [
      {
        text: $localize`:@@cancel:Cancel`,
        type: 'tertiary-text',
        onClick: () => this.cancelDelete(),
      },
      {
        text: $localize`:@@delete:Delete`,
        type: 'danger',
        disabled: this.saving,
        onClick: () => this.confirmDelete(),
      },
    ];
  }

  get typeNameDeleteButtons(): PopupButton[] {
    return [
      {
        text: $localize`:@@cancel:Cancel`,
        type: 'tertiary-text',
        onClick: () => this.cancelDelete(),
      },
      {
        text: $localize`:@@delete:Delete`,
        type: 'danger',
        disabled: this.typeNameConfirmDisabled || this.isSaving,
        onClick: () => this.confirmDelete(),
      },
    ];
  }

  get blockedDeleteButtons(): PopupButton[] {
    return [
      { text: $localize`:@@close:Close`, type: 'secondary', onClick: () => this.cancelDelete() },
    ];
  }

  get folderFormButtons(): PopupButton[] {
    return [
      {
        text: $localize`:@@cancel:Cancel`,
        type: 'secondary',
        onClick: () => { this.editPanelVisible = false; },
      },
      {
        text: this.saveFolderText,
        type: 'primary',
        submitBehavior: true,
        disabled: !this.selectedFolder.name?.trim() || this.saving,
        onClick: () => this.saveFolderFromPopup(),
      },
    ];
  }

  async saveFolderFromPopup(): Promise<void> {
    if (!this.selectedFolder.name?.trim() || this.saving) return;
    await this.submitFolderForm(null);
  }

  async submitFolderForm(e: any): Promise<void> {
    if (e) e.preventDefault();
    if (this.saving) return;
    this.saving = true;
    try {
      const payload = {
        Id: this.selectedFolder.id,
        Name: this.selectedFolder.name,
        ParentId: this.selectedFolder.parentId === this.virtualRootId ? null : this.selectedFolder.parentId,
      };
      let result: any;
      if (this.isSandbox) {
        result = this.isSandboxSite
          ? await this.sandboxService.writeSandboxSiteFolder(payload)
          : await this.sandboxService.writeSandboxProjectFolder(payload);
      } else {
        result = this.isSite
          ? await this.service.writeGridConnectionRepoFolder(payload)
          : await this.service.writeProjectRepoFolder(payload);
      }

      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      await this.reload();
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    } finally {
      this.saving = false;
    }
  }

  async initFolderDelete(folder: any): Promise<void> {
    this.selectedFolder = { id: folder.id, name: folder.name };
    this.typedFolderName = '';
    if (this.isSandbox) {
      this.deleteMode = 'simple';
      return;
    }
    try {
      const info = await this.service.getDocumentFolderDeleteInfo({ ItemId: folder.id });
      if (info.hasError === true) {
        AppGlobals.ShowErrorMessage(info.feedback);
        return;
      }
      this.deleteInfo = {
        documentCount: info.documentCount ?? 0,
        dependentTasks: info.dependentTasks ?? [],
      };
      this.deleteMode = this.resolveDeleteMode(this.deleteInfo);
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  /** Pure decision logic — unit tested in Task 13. */
  resolveDeleteMode(info: { documentCount: number; dependentTasks: any[] }): 'simple' | 'typename' | 'blocked' {
    if (info.dependentTasks?.length > 0) return 'blocked';
    if (info.documentCount > 0) return 'typename';
    return 'simple';
  }

  /** Pure gating logic — Delete button enabled only on exact name match. */
  get typeNameConfirmDisabled(): boolean {
    return this.typedFolderName !== (this.selectedFolder?.name ?? '');
  }

  dependentTaskLabel(task: any): string {
    if (task.isMicroTask) {
      return `${task.projectNr ?? ''} · ${task.projectName ?? ''} — ${task.name}`;
    }
    return task.name;
  }

  async confirmDelete(): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    try {
      const result = this.isSandbox
        ? await this.sandboxService.deleteSandboxFolder({ ItemId: this.selectedFolder.id })
        : await this.service.deleteDocumentFolder({ ItemId: this.selectedFolder.id });
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        await this.reload();
        return;
      }
      await this.reload();
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    } finally {
      this.saving = false;
    }
  }

  cancelDelete(): void {
    this.deleteMode = 'none';
    this.typedFolderName = '';
  }

  private collectIds(nodes: any[]): number[] {
    return nodes.flatMap(n => [n.id, ...this.collectIds(n.subFolders ?? [])]);
  }

  onPickerChanged(e: any): Promise<void> {
    return this.saveFolderSetting(e, this.isSite ? 'propertyFolderId' : 'imagesFolderId');
  }

  onCommentsPickerChanged(e: any): Promise<void> {
    return this.saveFolderSetting(e, 'commentsFolderId');
  }

  onMastrPickerChanged(e: any): Promise<void> {
    return this.saveFolderSetting(e, 'mastrFolderId');
  }

  // Re-fetches the current settings first so a pick cannot clobber the others; always sends all four ids.
  private async saveFolderSetting(e: any, key: 'propertyFolderId' | 'imagesFolderId' | 'commentsFolderId' | 'mastrFolderId'): Promise<void> {
    if (e.value === e.previousValue) return;
    try {
      const current = await this.service.getFolderSettings({});
      if (current.hasError === true) {
        AppGlobals.ShowErrorMessage(current.feedback);
        await this.reload();
        return;
      }
      const next = {
        propertyFolderId: current.propertyFolderId ?? null,
        imagesFolderId: current.imagesFolderId ?? null,
        commentsFolderId: current.commentsFolderId ?? null,
        mastrFolderId: current.mastrFolderId ?? null,
        [key]: e.value,
      };
      const result = await this.service.saveFolderSettings({
        PropertyFolderId: next.propertyFolderId,
        ImagesFolderId: next.imagesFolderId,
        CommentsFolderId: next.commentsFolderId,
        MastrFolderId: next.mastrFolderId,
      });
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        await this.reload();
        return;
      }
      this.settings = next;
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    }
  }
}
